"use client";

/**
 * MobileUserAvatar - Compact avatar button for mobile header
 *
 * Shows:
 * - Login icon when not authenticated (opens Privy login)
 * - User avatar when authenticated (opens Privy account modal via linkAccount workaround)
 */

import { usePrivy } from "@privy-io/react-auth";
import { LogIn } from "lucide-react";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { usePoints } from "@/contexts/PointsContext";
import { useAuth } from "@/hooks/useAuth";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { STATIC_ASSETS } from "@/lib/cdn";

export default function MobileUserAvatar() {
  const { ready, authenticated, user } = usePrivy();
  const { login } = useAuth();
  const { setMobileTargetRef } = usePoints();
  const { profile } = useUserProfile();
  const userImage = profile?.user?.image || null;
  const avatarRef = useRef<HTMLButtonElement>(null);

  // Belt-and-suspenders: register via callback (legacy path) AND mark
  // the element with data-fp-target-mobile so FloatingPoint can find
  // it via DOM query at animation time. The DOM query path is what
  // makes the animation 100% reliable across mount/unmount races.
  useEffect(() => {
    if (avatarRef.current) {
      setMobileTargetRef(avatarRef.current);
    }
    return () => setMobileTargetRef(null);
  }, [setMobileTargetRef]);

  // Loading state
  if (!ready) {
    return (
      <div className="w-8 h-8 rounded-[5px] bg-gray-200 dark:bg-[#18181D] animate-pulse" />
    );
  }

  // Not logged in - show login button
  if (!authenticated) {
    return (
      <button
        onClick={() => login()}
        className="w-8 h-8 rounded-[5px] bg-[#CCFF00] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        aria-label="Sign in"
      >
        <LogIn className="w-4 h-4 text-[#0A0A0A]" />
      </button>
    );
  }

  // Get user avatar from linked accounts or our API
  const getAvatarUrl = () => {
    // First try our own stored image
    if (userImage) return userImage;

    // Try Google (type assertion for profilePictureUrl which exists at runtime)
    const google = user?.google as { profilePictureUrl?: string } | undefined;
    if (google?.profilePictureUrl) return google.profilePictureUrl;

    // Try Twitter
    const twitter = user?.twitter;
    if (twitter?.profilePictureUrl) return twitter.profilePictureUrl;

    // Try Farcaster
    const farcaster = user?.farcaster;
    if (farcaster?.pfp) return farcaster.pfp;

    return null;
  };

  const avatarUrl = getAvatarUrl();

  // Logged in - show avatar
  // Note: The actual Privy modal is triggered by the off-screen UserPill in parent
  const handleAvatarClick = () => {
    // Find the Privy UserPill container and its button
    const privyContainer = document.querySelector('[data-privy-user-pill]');
    if (privyContainer) {
      // Temporarily make it clickable
      const container = privyContainer as HTMLElement;
      container.style.pointerEvents = 'auto';
      container.style.opacity = '1';

      // Find and click the button inside
      const button = container.querySelector('button');
      if (button) {
        button.click();
      }

      // Reset after a short delay
      setTimeout(() => {
        container.style.pointerEvents = 'none';
        container.style.opacity = '0';
      }, 100);
    }
  };

  return (
    <button
      ref={avatarRef}
      data-fp-target-mobile="true"
      onClick={handleAvatarClick}
      className="w-8 h-8 rounded-[5px] overflow-hidden border-2 border-[#CCFF00]/50 hover:border-[#CCFF00] active:scale-95 transition-all"
      aria-label="Account"
    >
      {avatarUrl ? (
        <Image
          src={avatarUrl}
          alt="Profile"
          width={32}
          height={32}
          sizes="32px"
          className="w-full h-full object-cover"
        />
      ) : (
        // 2026-06-03: default to the green Fren mascot (HD) instead of a
        // blank gradient+icon. Since the FrenPicker is no longer a
        // mandatory step, new users without a chosen Fren land here — the
        // branded avatar reads as "you're a Fren" rather than "missing photo".
        <Image
          src={STATIC_ASSETS.defaultAvatar}
          alt="Profile"
          width={32}
          height={32}
          sizes="32px"
          className="w-full h-full object-cover"
        />
      )}
    </button>
  );
}
